import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReadinessAssessment = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      id: 'technical_team',
      category: 'Technical Capability',
      question: "How would you rate your team\'s current AI/ML technical expertise?",
      options: [
        { value: 1, label: "Limited - Mostly using pre-built AI services", weight: 1 },
        { value: 2, label: "Basic - Some custom model development", weight: 2 },
        { value: 3, label: "Intermediate - Regular ML model training and deployment", weight: 3 },
        { value: 4, label: "Advanced - Deep learning and custom AI architectures", weight: 4 },
        { value: 5, label: "Expert - Research-level AI capabilities", weight: 5 }
      ]
    },
    {
      id: 'infrastructure',
      category: 'Infrastructure Readiness',
      question: "What\'s your current cloud/infrastructure setup?",
      options: [
        { value: 1, label: "Mostly SaaS solutions, minimal infrastructure control", weight: 1 },
        { value: 2, label: "Basic cloud services with some custom deployments", weight: 2 },
        { value: 3, label: "Hybrid cloud with container orchestration", weight: 3 },
        { value: 4, label: "Advanced cloud-native with Kubernetes and CI/CD", weight: 4 },
        { value: 5, label: "Multi-cloud with advanced DevOps and MLOps pipelines", weight: 5 }
      ]
    },
    {
      id: 'data_maturity',
      category: 'Data Management',
      question: "How mature is your data infrastructure and governance?",
      options: [
        { value: 1, label: "Data scattered across systems, limited governance", weight: 1 },
        { value: 2, label: "Basic data warehouse with some standardization", weight: 2 },
        { value: 3, label: "Data lake with governance policies in place", weight: 3 },
        { value: 4, label: "Modern data platform with real-time capabilities", weight: 4 },
        { value: 5, label: "Advanced data mesh with comprehensive governance", weight: 5 }
      ]
    },
    {
      id: 'budget_control',
      category: 'Financial Flexibility',
      question: "How much control do you have over AI technology budget allocation?",
      options: [
        { value: 1, label: "Limited - Mostly fixed vendor contracts", weight: 1 },
        { value: 2, label: "Some flexibility within existing budget", weight: 2 },
        { value: 3, label: "Moderate - Can reallocate between tools/services", weight: 3 },
        { value: 4, label: "High - Significant budget discretion for new approaches", weight: 4 },
        { value: 5, label: "Complete - Full authority over AI technology investments", weight: 5 }
      ]
    },
    {
      id: 'vendor_dependency',
      category: 'Current Dependencies',
      question: "How dependent are you currently on proprietary AI vendors?",
      options: [
        { value: 5, label: "Heavily dependent - Most AI through single vendor", weight: 1 },
        { value: 4, label: "Significant - Multiple vendors but tightly integrated", weight: 2 },
        { value: 3, label: "Moderate - Mix of vendors and some internal tools", weight: 3 },
        { value: 2, label: "Limited - Mostly internal with some vendor services", weight: 4 },
        { value: 1, label: "Minimal - Primarily internal AI capabilities", weight: 5 }
      ]
    },
    {
      id: 'compliance_requirements',
      category: 'Regulatory Environment',
      question: "How strict are your compliance and regulatory requirements?",
      options: [
        { value: 1, label: "Minimal - Standard business compliance only", weight: 3 },
        { value: 2, label: "Moderate - Industry-specific regulations", weight: 4 },
        { value: 3, label: "High - Financial services or healthcare compliance", weight: 5 },
        { value: 4, label: "Very High - Government or defense requirements", weight: 5 },
        { value: 5, label: "Extreme - National security or critical infrastructure", weight: 5 }
      ]
    },
    {
      id: 'innovation_pace',
      category: 'Innovation Requirements',
      question: "How important is rapid AI innovation and customization to your competitive advantage?",
      options: [
        { value: 1, label: "Low - Standard AI capabilities are sufficient", weight: 2 },
        { value: 2, label: "Moderate - Some customization needed", weight: 3 },
        { value: 3, label: "High - Custom AI is competitive differentiator", weight: 4 },
        { value: 4, label: "Very High - AI innovation is core to business model", weight: 5 },
        { value: 5, label: "Critical - AI leadership defines market position", weight: 5 }
      ]
    },
    {
      id: 'risk_tolerance',
      category: 'Risk Management',
      question: "What\'s your organization\'s tolerance for technology risk and change?",
      options: [
        { value: 1, label: "Very Low - Prefer proven, stable solutions", weight: 2 },
        { value: 2, label: "Low - Cautious adoption of new technologies", weight: 3 },
        { value: 3, label: "Moderate - Balanced approach to innovation", weight: 4 },
        { value: 4, label: "High - Early adopter of promising technologies", weight: 5 },
        { value: 5, label: "Very High - Technology leader and innovator", weight: 5 }
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < questions?.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResults();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResults = () => {
    let totalScore = 0;
    let maxScore = 0;

    questions?.forEach(question => {
      const answer = answers?.[question?.id];
      if (answer) {
        totalScore += answer?.weight;
      }
      maxScore += 5;
    });

    const percentage = (totalScore / maxScore) * 100;
    setShowResults({ score: totalScore, maxScore, percentage });
  };

  const getReadinessLevel = (percentage) => {
    if (percentage >= 80) return { level: "Excellent", color: "text-green-600", bg: "bg-green-50", border: "border-green-200" };
    if (percentage >= 60) return { level: "Good", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" };
    if (percentage >= 40) return { level: "Moderate", color: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200" };
    return { level: "Developing", color: "text-red-600", bg: "bg-red-50", border: "border-red-200" };
  };

  const getRecommendations = (percentage) => {
    if (percentage >= 80) {
      return {
        title: "Ready for Immediate Open-Source AI Adoption",
        description: "Your organization has excellent readiness for open-source AI implementation.",
        actions: [
          "Begin with pilot projects in non-critical areas",
          "Establish internal AI Center of Excellence",
          "Start migrating from proprietary solutions",
          "Implement comprehensive MLOps pipeline"
        ],
        timeline: "3-6 months to full implementation"
      };
    } else if (percentage >= 60) {
      return {
        title: "Good Foundation - Prepare for Strategic Migration",
        description: "You have solid capabilities but should strengthen key areas before full adoption.",
        actions: [
          "Invest in team training and skill development",
          "Upgrade infrastructure and tooling",
          "Start with proof-of-concept projects",
          "Develop internal AI governance framework"
        ],
        timeline: "6-12 months preparation + implementation"
      };
    } else if (percentage >= 40) {
      return {
        title: "Building Readiness - Strategic Preparation Phase",
        description: "Focus on capability building before major open-source AI initiatives.",
        actions: [
          "Hire or train AI/ML specialists",
          "Modernize data infrastructure",
          "Establish cloud-native capabilities",
          "Create AI strategy and roadmap"
        ],
        timeline: "12-18 months preparation phase"
      };
    } else {
      return {
        title: "Foundation Building - Long-term Strategic Planning",
        description: "Significant preparation needed before open-source AI adoption.",
        actions: [
          "Develop comprehensive AI strategy",
          "Build technical team and capabilities",
          "Modernize infrastructure and data systems",
          "Start with AI education and awareness"
        ],
        timeline: "18-24 months foundation building"
      };
    }
  };

  if (showResults) {
    const readiness = getReadinessLevel(showResults?.percentage);
    const recommendations = getRecommendations(showResults?.percentage);

    return (
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-brand-primary mb-6">
              Your Open-Source AI Readiness Assessment
            </h2>
          </div>

          {/* Score Display */}
          <div className={`${readiness?.bg} ${readiness?.border} border-2 rounded-2xl p-8 mb-8`}>
            <div className="text-center">
              <div className="text-6xl font-bold mb-4 ${readiness.color}">
                {Math.round(showResults?.percentage)}%
              </div>
              <div className={`text-2xl font-bold mb-2 ${readiness?.color}`}>
                {readiness?.level} Readiness
              </div>
              <div className="text-gray-600">
                Score: {showResults?.score} out of {showResults?.maxScore}
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-brand-primary mb-4">
              {recommendations?.title}
            </h3>
            <p className="text-gray-700 mb-6 text-lg">
              {recommendations?.description}
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-bold text-brand-primary mb-4">
                  Recommended Actions
                </h4>
                <ul className="space-y-3">
                  {recommendations?.actions?.map((action, index) => (
                    <li key={index} className="flex items-start">
                      <Icon name="CheckCircle" size={16} className="text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-bold text-brand-primary mb-4">
                  Implementation Timeline
                </h4>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center mb-2">
                    <Icon name="Clock" size={16} className="text-brand-accent mr-2" />
                    <span className="font-semibold">Estimated Timeline</span>
                  </div>
                  <span className="text-brand-accent font-bold">{recommendations?.timeline}</span>
                </div>

                <div className="mt-6">
                  <Button
                    variant="default"
                    fullWidth
                    className="bg-brand-primary hover:bg-brand-primary/90"
                    iconName="Calendar"
                    iconPosition="left"
                  >
                    Schedule Strategy Session
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-brand-primary rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Take the Next Step?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Get a personalized open-source AI strategy based on your assessment results. 
              Our experts will help you create a detailed implementation roadmap.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900"
                iconName="Download"
                iconPosition="left"
              >
                Get Custom Strategy Report
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-brand-primary"
                iconName="MessageCircle"
                iconPosition="left"
              >
                Discuss Results with Expert
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const currentQ = questions?.[currentQuestion];
  const progress = ((currentQuestion + 1) / questions?.length) * 100;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-brand-primary mb-6">
            Open-Source AI Readiness Assessment
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Evaluate your organization's readiness to adopt open-source AI solutions. 
            Get personalized recommendations based on your current capabilities.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Question {currentQuestion + 1} of {questions?.length}
            </span>
            <span className="text-sm font-medium text-brand-primary">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-brand-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-gray-50 rounded-2xl p-8 mb-8">
          <div className="mb-6">
            <div className="text-sm font-medium text-brand-primary mb-2">
              {currentQ?.category}
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {currentQ?.question}
            </h3>
          </div>

          <div className="space-y-3">
            {currentQ?.options?.map((option) => (
              <button
                key={option?.value}
                onClick={() => handleAnswer(currentQ?.id, option)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                  answers?.[currentQ?.id]?.value === option?.value
                    ? 'border-brand-primary bg-brand-primary/5 text-brand-primary' :'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 ${
                    answers?.[currentQ?.id]?.value === option?.value
                      ? 'border-brand-primary bg-brand-primary' :'border-gray-300'
                  }`}>
                    {answers?.[currentQ?.id]?.value === option?.value && (
                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                    )}
                  </div>
                  <span>{option?.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={prevQuestion}
            disabled={currentQuestion === 0}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>

          <Button
            variant="default"
            onClick={nextQuestion}
            disabled={!answers?.[currentQ?.id]}
            className="bg-brand-primary hover:bg-brand-primary/90"
            iconName={currentQuestion === questions?.length - 1 ? "BarChart" : "ChevronRight"}
            iconPosition="right"
          >
            {currentQuestion === questions?.length - 1 ? "View Results" : "Next"}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ReadinessAssessment;